import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Center,
  Divider,
  Group,
  Image,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from "@mantine/core";
import { IconStarFilled } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { fetchTaskProductImages } from "@/utils/api";
import { MISSING_IMAGE_PLACEHOLDER } from "../../config/constants";

type ProductImagesModalProps = {
  taskId: string | null;
  /** Product whose images to show; null keeps the modal closed. */
  product: { id: string; title: string } | null;
  onClose: () => void;
};

/** Modal showing a large main image with a clickable thumbnail strip of all a product's images. */
export const ProductImagesModal = ({
  taskId,
  product,
  onClose,
}: ProductImagesModalProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  useEffect(() => setActiveIndex(0), [product]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["product-images", product?.id],
    queryFn: () => fetchTaskProductImages(taskId ?? "", product?.id ?? ""),
    enabled: !!taskId && !!product,
  });

  // Primary image first (and thus the default selected one); rest keep their order.
  const images = useMemo(
    () =>
      (data ?? [])
        .filter((img) => img.signed_url)
        .sort((a, b) => Number(b.is_primary) - Number(a.is_primary)),
    [data],
  );
  const active = Math.min(activeIndex, Math.max(images.length - 1, 0));

  return (
    <Modal
      opened={product !== null}
      onClose={onClose}
      size="xl"
      centered
      withinPortal={false}
      title={
        <Text fw={600} lineClamp={1}>
          {product?.title}
        </Text>
      }
      styles={{ title: { flex: 1, textAlign: "center" } }}
    >
      {isLoading ? (
        <Center h={480}>
          <Loader />
        </Center>
      ) : isError ? (
        <Center h={480}>
          <Text c="dimmed">Failed to load images.</Text>
        </Center>
      ) : images.length === 0 ? (
        <Center h={480}>
          <Text c="dimmed">No images for this product.</Text>
        </Center>
      ) : (
        <Stack gap="md">
          <Divider />

          <Box pos="relative">
            <Image
              src={images[active].signed_url}
              h="55vh"
              fit="contain"
              fallbackSrc={MISSING_IMAGE_PLACEHOLDER}
            />
            {images[active].is_primary && (
              <Badge
                pos="absolute"
                top={8}
                left={8}
                variant="filled"
                leftSection={<IconStarFilled size={12} />}
              >
                Primary
              </Badge>
            )}
          </Box>

          <Divider />
          <ScrollArea type="hover" offsetScrollbars>
            <Group justify="center" gap="sm" wrap="nowrap">
              {images.map((img, index) => (
                <UnstyledButton
                  key={img.id}
                  pos="relative"
                  onClick={() => setActiveIndex(index)}
                >
                  <Paper
                    withBorder
                    radius="md"
                    p={4}
                    style={{
                      borderWidth: 2,
                      borderColor:
                        index === active
                          ? "var(--mantine-primary-color-filled)"
                          : undefined,
                    }}
                  >
                    <Image
                      src={img.signed_url}
                      w={72}
                      h={72}
                      fit="contain"
                      fallbackSrc={MISSING_IMAGE_PLACEHOLDER}
                    />
                  </Paper>
                  {img.is_primary && (
                    <ThemeIcon
                      pos="absolute"
                      top={-6}
                      right={-6}
                      radius="xl"
                      size="sm"
                      variant="filled"
                    >
                      <IconStarFilled size={11} />
                    </ThemeIcon>
                  )}
                </UnstyledButton>
              ))}
            </Group>
          </ScrollArea>
        </Stack>
      )}
    </Modal>
  );
};
